import React, { useState } from 'react';
import { 
  Camera, 
  Lock, 
  MessageSquare, 
  X, 
  Save, 
  AlertCircle, 
  CheckCircle2, 
  UploadCloud,
  Shield,
  Settings,
  User,
  LifeBuoy
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, db, updateUserPassword, uploadBytesToStorage } from '../firebase';
import { addDoc, collection } from 'firebase/firestore';

// --- REUSABLE PANEL COMPONENT ---
const Panel = ({ title, icon: Icon, children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={`bg-slate-800/50 border border-slate-700/50 rounded-xl shadow-xl ${className}`}
  >
    {title && (
      <h3 className="flex items-center text-lg font-semibold text-white mb-4 px-6 pt-6 border-b border-slate-700/50 pb-4">
        {Icon && <Icon size={20} className="mr-2 text-slate-100" />}
        <span className="ml-0">{title}</span>
      </h3>
    )}
    <div className={title ? "px-6 pb-6 pt-4" : "p-6"}>{children}</div>
  </motion.div>
);

// --- REUSABLE DANTE BUTTON COMPONENT ---
// Matches the exact styling of the SignInPage button
const DanteButton = ({ children, onClick, type = "button", disabled = false, className = "", icon: Icon }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`
      flex justify-center items-center py-3 px-4 
      border border-transparent rounded-lg shadow-sm 
      text-md font-semibold text-slate-100 
      bg-slate-700 hover:bg-slate-600 
      hover:drop-shadow-[0_0_5px_rgba(203,213,225,0.2)] 
      active:bg-gradient-to-br active:from-slate-700 active:to-slate-800 
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 focus:ring-offset-slate-900 
      transition duration-300 uppercase tracking-wide
      disabled:opacity-70 disabled:cursor-not-allowed
      ${className}
    `}
  >
    {Icon && <Icon className="w-4 h-4 mr-2" />}
    {children}
  </button>
);

const SettingsPage = ({campaignId}) => {
  // --- State Management ---
  const [profileImage, setProfileImage] = useState("https://api.dicebear.com/7.x/avataaars/svg?seed=Felix");
  const [isUploading, setIsUploading] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    description: ''
  });
  const [ticketStatus, setTicketStatus] = useState('idle');

  // --- Handlers ---
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
      setTimeout(() => {
        const objectUrl = URL.createObjectURL(file);
        uploadBytesToStorage(`/campaigns/${campaignId}/candidate_image`,file);
        setProfileImage(objectUrl);
        setIsUploading(false);
      }, 1000);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError("New passwords do not match.");
      return;
    }
    if (passwordForm.new.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }
    updateUserPassword(passwordForm.confirm);
    setIsPasswordModalOpen(false);
    setPasswordForm({ current: '', new: '', confirm: '' });
  };

  const handleTicketSubmit = (e) => {
    
    setTicketStatus('submitting');
    const {description, ...data} = ticketForm;
    addDoc(collection(db,"users",auth.currentUser.uid,"tickets"),{...data, messages: [description]}).then(() => {
      console.hidden("Ticket Data Payload:", ticketForm);
      setTicketStatus('success');
      setTicketForm({ subject: '', category: 'general', priority: 'medium', description: '' });
      setTimeout(() => setTicketStatus('idle'), 3000);
    });
  };

  // Helper for Priority Colors
  const getPriorityStyles = (level, selected) => {
    if (level !== selected) {
      return 'bg-slate-900/50 border-slate-700 text-slate-500 hover:bg-slate-800 hover:text-slate-300';
    }
    switch (level) {
      case 'low': return 'bg-green-500/10 border-green-600 text-green-400 ring-1 ring-green-600/50 shadow-[0_0_10px_rgba(34,197,94,0.1)]';
      case 'medium': return 'bg-orange-500/10 border-orange-500 text-orange-400 ring-1 ring-orange-500/50 shadow-[0_0_10px_rgba(249,115,22,0.1)]';
      case 'high': return 'bg-red-500/10 border-red-600 text-red-400 ring-1 ring-red-600/50 shadow-[0_0_10px_rgba(239,68,68,0.1)]';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6 lg:p-10 font-sans text-slate-300 relative">
      {/* Background radial gradient matches sign in page */}
      <div className="absolute inset-0 z-0 opacity-20 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800 via-slate-950 to-slate-950 pointer-events-none"></div>
      
      {/* Page Header */}
      <motion.header 
        initial={{ y: -20, opacity: 0 }} 
        animate={{ y: 0, opacity: 1 }} 
        className="max-w-6xl mx-auto mb-10 border-b border-slate-700 pb-6 relative z-10"
      >
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Account Settings</h1>
          <p className="text-slate-400 mt-1">Manage your candidate profile, security, and support requests.</p>
        </div>
      </motion.header>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
        
        {/* LEFT COLUMN: Profile & Security */}
        <div className="lg:col-span-1 space-y-8">
          
          {/* 1. Candidate Image Upload Card */}
          <Panel title="Candidate Profile" icon={User}>
            <div className="flex flex-col items-center text-center">
              <div className="relative group">
                <div className={`w-32 h-32 rounded-full overflow-hidden ring-4 ring-slate-700 mb-4 relative bg-slate-800 ${isUploading ? 'opacity-50' : ''}`}>
                  <img 
                    src={profileImage} 
                    alt="Profile" 
                    className="w-full h-full object-cover" 
                  />
                </div>
                
                {/* Hover Overlay for Upload */}
                <label className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Camera className="w-8 h-8 text-white" />
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleImageChange}
                  />
                </label>
                
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400"></div>
                  </div>
                )}
              </div>
              
              <h3 className="font-semibold text-lg text-white">Candidate Photo</h3>
              <p className="text-xs text-slate-500 mt-1 mb-6">Allowed *.jpeg, *.jpg, *.png, max 3MB</p>
              
              {/* Upload Button */}
              <label className="w-full">
                <DanteButton onClick={() => {}} className="w-full" icon={UploadCloud}>
                  Upload New Image
                </DanteButton>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </label>
            </div>
          </Panel>

          {/* 2. Security / Change Password Trigger */}
          <Panel title="Security" icon={Shield}>
            <p className="text-sm text-slate-400 mb-6">
              Ensure your account is secure. We recommend rotating your password every 90 days.
            </p>
            <DanteButton onClick={() => setIsPasswordModalOpen(true)} className="w-full" icon={Lock}>
              Change Password
            </DanteButton>
          </Panel>

        </div>

        {/* RIGHT COLUMN: Support Ticket Form */}
        <div className="lg:col-span-2">
          <Panel title="Support Center" icon={LifeBuoy} className="h-full">
            <div className="mb-6">
              <p className="text-sm text-slate-400">Raise a ticket regarding account access, data issues, or feature requests.</p>
            </div>

            {ticketStatus === 'success' ? (
              <div className="h-64 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in duration-300">
                <div className="w-16 h-16 bg-green-500/10 text-green-400 rounded-full flex items-center justify-center mb-4 border border-green-500/20 shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-bold text-white">Ticket Received!</h4>
                <p className="text-slate-500 mt-2 max-w-xs mx-auto">We have received your request and our support team will contact you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleTicketSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Subject</label>
                    <input 
                      required
                      type="text" 
                      className="w-full p-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-1 focus:ring-slate-400 focus:border-slate-400 outline-none transition-all placeholder-slate-600"
                      placeholder="e.g, Can I refresh my data?"
                      value={ticketForm.subject}
                      onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Category</label>
                    <div className="relative">
                      <select 
                        className="w-full p-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-1 focus:ring-slate-400 focus:border-slate-400 outline-none transition-all appearance-none cursor-pointer"
                        value={ticketForm.category}
                        onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                      >
                        <option value="general">General Inquiry</option>
                        <option value="bug">Report a Bug</option>
                        <option value="billing">Billing / Subscription</option>
                        <option value="account">Account Access</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-slate-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Priority Level</label>
                  <div className="flex gap-4 mt-1">
                    {['low', 'medium', 'high'].map((p) => (
                      <label key={p} className={`
                        flex-1 border rounded-lg p-2 text-center cursor-pointer text-sm font-medium capitalize transition-all duration-200
                        ${getPriorityStyles(p, ticketForm.priority)}
                      `}>
                        <input 
                          type="radio" 
                          name="priority" 
                          value={p} 
                          className="hidden"
                          onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                        />
                        {p}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Description</label>
                  <div className="relative">
                    <MessageSquare className="absolute top-3 left-3 w-5 h-5 text-slate-500" />
                    <textarea 
                      required
                      rows="6"
                      className="w-full pl-10 p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:ring-1 focus:ring-slate-400 focus:border-slate-400 outline-none transition-all resize-none placeholder-slate-600"
                      placeholder="Please provide details about the issue..."
                      value={ticketForm.description}
                      onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                    ></textarea>
                  </div>
                </div>

                <div className="pt-2">
                  <DanteButton  onClick={handleTicketSubmit} disabled={ticketStatus === 'submitting'} className="w-full md:w-auto px-8" icon={Save}>
                    {ticketStatus === 'submitting' ? 'Processing...' : 'Submit Ticket'}
                  </DanteButton>
                </div>
              </form>
            )}
          </Panel>
        </div>
      </div>

      {/* --- MODALS / DIALOGS --- */}

      {/* Change Password Modal */}
      <AnimatePresence>
        {isPasswordModalOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setIsPasswordModalOpen(false)}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              // Added drop-shadow similar to SignInPage card glow
              className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl drop-shadow-[0_0_15px_rgba(203,213,225,0.2)] w-full max-w-md m-4 p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              
              <button 
                onClick={() => setIsPasswordModalOpen(false)}
                className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-slate-800 text-slate-100 border border-slate-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                  <Lock className="w-7 h-7" />
                </div>
                <h2 className="text-2xl font-bold text-white tracking-tight">Update Password</h2>
                <p className="text-sm text-slate-400 mt-2">Choose a strong password to keep your account safe.</p>
              </div>

              {passwordError && (
                <div className="mb-6 p-3 bg-red-900/30 border border-red-800/50 rounded-lg flex items-center gap-2 text-sm text-red-300">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {passwordError}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Current Password</label>
                  <input 
                    type="password" 
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder-slate-600 transition-all"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">New Password</label>
                  <input 
                    type="password" 
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder-slate-600 transition-all"
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Confirm New Password</label>
                  <input 
                    type="password" 
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder-slate-600 transition-all"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                  />
                </div>
                
                <div className="pt-4 flex gap-3">
                  <button 
                    type="button"
                    onClick={() => setIsPasswordModalOpen(false)}
                    className="flex-1 px-4 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800 hover:text-white font-medium transition-colors uppercase tracking-wide text-sm"
                  >
                    Cancel
                  </button>
                  <DanteButton type="submit" className="flex-1 py-3 text-sm">
                    Update
                  </DanteButton>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default SettingsPage;